import { useEffect, useState } from "react";
import { ProgressSummaryCard } from "@/pages/MaProgression";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen,
  Users as UsersIcon,
  GraduationCap,
  ClipboardText,
  Trophy,
  Sparkle,
  Student,
  Clock,
  Question,
  CaretRight,
  Play,
  Calendar as CalendarIcon,
  ChatCircle,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { useAuth } from "@/context/AuthContext";
import api, { coverSrc } from "@/lib/api";

// Dérivées des variables CSS du thème (index.css) — restent synchronisées
// avec la palette de l'app plutôt que des couleurs codées en dur.
const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function HeroStat({ icon: Icon, label, value, note }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex items-center gap-5 rounded-xl border border-border bg-primary p-6 text-primary-foreground sm:col-span-2"
    >
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/15">
        <Icon size={30} weight="duotone" />
      </div>
      <div>
        <p className="font-display text-4xl font-extrabold leading-none tracking-tight">{value}</p>
        <p className="mt-1.5 text-sm opacity-90">{label}</p>
        {note && <p className="mt-0.5 text-xs opacity-70">{note}</p>}
      </div>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value, accent, color, i = 0 }) {
  const varName = color || (accent ? "--accent" : "--primary");
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 * (i + 1) }}
      className="rounded-xl border border-border bg-card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg"
    >
      <div
        className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg"
        style={{ backgroundColor: `hsl(var(${varName}) / 0.12)`, color: `hsl(var(${varName}))` }}
      >
        <Icon size={24} weight="duotone" />
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-muted-foreground">Chargement...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Tableau de bord</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Bonjour, {user.name.split(" ")[0]} 👋</h1>
      </div>

      {stats.role === "admin" && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <HeroStat icon={UsersIcon} label="Utilisateurs sur la plateforme" value={stats.users} note={`${stats.students} étudiants · ${stats.teachers} professeurs`} />
            <Stat icon={BookOpen} label="Cours publiés" value={stats.published_courses} i={0} />
            <Stat icon={GraduationCap} label="Professeurs" value={stats.teachers} accent i={1} />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Répartition des rôles</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={stats.by_role} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3}>
                    {stats.by_role.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4">
                {stats.by_role.map((r, i) => (
                  <span key={r.name} className="flex items-center gap-2 text-sm">
                    <span className="h-3 w-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    {r.name} ({r.value})
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Aperçu de la plateforme</h3>
              <div className="mt-4 space-y-3">
                {[
                  ["Total des cours", stats.courses],
                  ["Groupes", stats.groups],
                  ["Cours publiés", stats.published_courses],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between rounded-lg bg-secondary p-4">
                    <span className="text-sm text-muted-foreground">{l}</span>
                    <span className="font-display text-xl font-bold text-primary">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {stats.role === "professeur" && (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <HeroStat
              icon={ClipboardText}
              label="Copies à corriger"
              value={stats.pending_grading}
              note={stats.pending_grading > 0 ? "En attente de votre correction" : "Tout est corrigé, bravo !"}
            />
            <Stat icon={BookOpen} label="Mes cours" value={stats.my_courses} i={0} />
            <Stat icon={Student} label="Mes étudiants" value={stats.students} i={1} />
            <Stat icon={Sparkle} label="Quiz créés" value={stats.quizzes} i={2} />
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold">Étudiants par cours</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.course_stats}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="étudiants" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {stats.role === "etudiant" && (
        <>
          {stats.continue_learning && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate(stats.continue_learning.link)}
              className="group flex w-full items-center gap-5 overflow-hidden rounded-xl border border-border bg-gradient-to-r from-primary to-[hsl(220,55%,32%)] p-6 text-left text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5"
              data-testid="continue-learning-card"
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Play size={26} weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] opacity-80">Reprendre où vous en étiez</p>
                <p className="mt-0.5 truncate font-display text-xl font-bold">{stats.continue_learning.lesson_title}</p>
                <p className="truncate text-sm opacity-80">{stats.continue_learning.course_title}</p>
              </div>
              <CaretRight size={22} className="shrink-0 opacity-80 transition-transform group-hover:translate-x-1" />
            </motion.button>
          )}

          <ProgressSummaryCard />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Mes cours", icon: BookOpen, to: "/app/cours", color: "hsl(var(--primary))" },
              { label: "Mes notes", icon: GraduationCap, to: "/app/notes", color: "hsl(var(--campus-green))" },
              { label: "Calendrier", icon: CalendarIcon, to: "/app/calendrier", color: "hsl(var(--campus-orange))" },
              { label: "Messagerie", icon: ChatCircle, to: "/app/messagerie", color: "hsl(var(--accent))" },
            ].map((qa) => (
              <button
                key={qa.to}
                onClick={() => navigate(qa.to)}
                className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-secondary"
                data-testid={`quick-action-${qa.to.replace("/app/", "")}`}
              >
                <qa.icon size={22} weight="duotone" style={{ color: qa.color }} />
                <span className="text-xs font-medium">{qa.label}</span>
              </button>
            ))}
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <HeroStat
              icon={Trophy}
              label="Moyenne générale"
              value={`${stats.average}/20`}
              note={stats.average >= 14 ? "Excellent travail, continuez !" : "Chaque devoir compte, courage !"}
            />
            <Stat icon={BookOpen} label="Cours suivis" value={stats.my_courses} color="--primary" i={0} />
            <Stat icon={ClipboardText} label="Notes reçues" value={stats.graded_items} color="--campus-green" i={1} />
            <Stat icon={Sparkle} label="Quiz passés" value={stats.quiz_attempts} color="--campus-orange" i={2} />
          </div>

          {stats.this_week && stats.this_week.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold">
                <Clock size={20} className="text-accent" /> Cette semaine
              </h3>
              <div className="mt-3 space-y-2">
                {stats.this_week.map((item, i) => {
                  const days = Math.round((new Date(item.due_date) - new Date(new Date().toDateString())) / 86400000);
                  const urgent = days <= 1;
                  return (
                    <button
                      key={i}
                      onClick={() => navigate(item.link)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-secondary"
                      data-testid={`this-week-item-${i}`}
                    >
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${urgent ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
                        {item.type === "quiz" ? <Question size={18} weight="duotone" /> : <ClipboardText size={18} weight="duotone" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{item.title}</p>
                        <p className="truncate text-xs text-muted-foreground">{item.course_title}</p>
                      </div>
                      <span className={`shrink-0 text-xs font-semibold ${urgent ? "text-accent" : "text-muted-foreground"}`}>
                        {days <= 0 ? "Aujourd'hui" : days === 1 ? "Demain" : `Dans ${days} j.`}
                      </span>
                      <CaretRight size={14} className="shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stats.my_courses_list && stats.my_courses_list.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold">Mes cours</h3>
                <button onClick={() => navigate("/app/cours")} className="text-xs font-medium text-primary hover:underline">Tout voir</button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {stats.my_courses_list.slice(0, 6).map((c, i) => {
                  const palette = ["--primary", "--campus-green", "--campus-orange", "--accent"];
                  const varName = palette[i % palette.length];
                  return (
                    <motion.button
                      key={c.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      onClick={() => navigate(`/app/cours/${c.id}`)}
                      className="flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-secondary"
                      data-testid={`my-course-${c.id}`}
                    >
                      {coverSrc(c.cover_image) ? (
                        <img src={coverSrc(c.cover_image)} alt="" className="h-10 w-14 shrink-0 rounded object-cover" />
                      ) : (
                        <div
                          className="flex h-10 w-14 shrink-0 items-center justify-center rounded font-display text-xs font-bold"
                          style={{ backgroundColor: `hsl(var(${varName}) / 0.12)`, color: `hsl(var(${varName}))` }}
                        >
                          {(c.code || c.title || "?").slice(0, 4)}
                        </div>
                      )}
                      <span className="truncate text-sm font-medium">{c.title}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {stats.recent_grades && stats.recent_grades.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Dernières notes reçues</h3>
              <div className="mt-3 space-y-2">
                {stats.recent_grades.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(g.link)}
                    className="flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-secondary"
                    data-testid={`recent-grade-${i}`}
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--campus-green))]/10 text-[hsl(var(--campus-green))]">
                      <CheckCircle size={18} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium">{g.assignment_title}</p>
                        <span className="shrink-0 font-display font-bold text-primary">{g.grade}/{g.max_points}</span>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">{g.course_title}</p>
                      {g.feedback && <p className="mt-1 truncate text-xs italic text-muted-foreground">« {g.feedback} »</p>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold">Évolution de mes notes</h3>
            {stats.grade_progress.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Pas encore de notes.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={stats.grade_progress}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis domain={[0, 20]} fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="note" stroke="hsl(var(--accent))" strokeWidth={3} dot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
